import * as React from 'react';
import AttributesSection from '../../library/attributes-section';
import SearchAttributes from './SearchAttributes';
import HeadingSection from './HeadingSection';
import { buildHeadings, headingFieldCount, initialOpenHeadings } from './headings';
import { buildSearchIndex } from './search';
import { sectionKeyFor } from '../../library/attributes-section/util';
import { mergeParams } from './mergeParams';
import { anyOpen, openSection, setAllSections, toggleSection } from './openSections';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  SaveButton,
  SecondaryButton,
  PrimaryButton,
} from "../../library/Buttons";
import cn from "classnames";
import useApi from "../../hooks/useApi";

import './styles';

// Heading height is spring-animated, so a scroll that starts before it settles centres the
// collapsed header instead of the row
const EXPAND_SETTLE_MS = 400;

export default function Attributes(props) {
  const { saving, error, dismissError, response, ...api } = useApi();
  const [savedMessage, setSavedMessage] = React.useState(null);
  const [showSavedMessage, setShowSavedMessage] = React.useState(null);
  const [savedHasError, setSavedHasError] = React.useState(null);

  const headings = React.useMemo(
    () => buildHeadings(props.sections, props.groups),
    [props.sections, props.groups]
  );

  const initialParams = (sections) => {
    const collection = {};
    sections.forEach((section) => {
      collection[section.rootAttributeKey] = {
        ...collection[section.rootAttributeKey],
        ...section.data
      };
    })

    return collection;
  }

  const [params, setParams] = React.useState(() => initialParams(props.sections || []));
  const [highlightId, setHighlightId] = React.useState(null);
  const [scrollTarget, setScrollTarget] = React.useState(null);
  const [openHeadings, setOpenHeadings] = React.useState(() => initialOpenHeadings(headings));

  const searchIndex = React.useMemo(() => buildSearchIndex(props.sections), [props.sections]);

  const jumpToAttribute = (entry) => {
    const wasOpen = Boolean(openHeadings[entry.headingKey]);

    setOpenHeadings((prev) => openSection(prev, entry.headingKey));
    setScrollTarget({ entry, delay: wasOpen ? 0 : EXPAND_SETTLE_MS });
  };

  React.useEffect(
    () => {
      if (!scrollTarget) return;

      const { entry, delay } = scrollTarget;

      const timer = setTimeout(() => {
        const target =
          document.getElementById(entry.domId) || document.getElementById(entry.anchorId);

        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHighlightId(entry.domId);
        }

        setScrollTarget(null);
      }, delay);

      return () => clearTimeout(timer);
    },
    [scrollTarget]
  );

  React.useEffect(
    () => {
      if (!highlightId) return;

      const clearHighlight = setTimeout(() => setHighlightId(null), 2500);
      return () => clearTimeout(clearHighlight);
    },
    [highlightId]
  );

  const onChange = (attributes) => {
    if (!attributes) return;
    setParams((prevParams) => mergeParams(prevParams, attributes));
  }

  const setResponseStatus = (message, hasError) => {
    setSavedMessage(message);
    setSavedHasError(hasError);
    setShowSavedMessage(true);

    setTimeout(() => {
      setShowSavedMessage(false);
      setSavedHasError(null);
    }, 4000);
  }

  const onSuccess = () => { setResponseStatus("Saved Successfully!", false) }
  const onFailure = () => { setResponseStatus("Error Saving Place", true) }

  const persistChanges = (e) => {
    if (e) e.preventDefault();
    api[props.method || "patch"](
      { listing: params },
      onSuccess,
      onFailure
    );
  };

  return (
    <React.Fragment>
      <div data-cy="attributes" className="editor__attributes-container">
        <div className="editor__attributes-container__section-controls">
          <PrimaryButton
            cy="toggle-all-sections"
            onClick={() => setOpenHeadings((prev) => setAllSections(prev, !anyOpen(prev)))}
          >
            <FontAwesomeIcon icon={anyOpen(openHeadings) ? 'chevron-up' : 'chevron-down'} />
            {anyOpen(openHeadings) ? ' Collapse all' : ' Expand all'}
          </PrimaryButton>
        </div>

        <SearchAttributes index={searchIndex} onSelect={jumpToAttribute} />

        {headings.map((heading) => (
          <HeadingSection
            key={heading.key}
            name={heading.name}
            fieldCount={headingFieldCount(heading)}
            expanded={!!openHeadings[heading.key]}
            onToggle={() => setOpenHeadings((prev) => toggleSection(prev, heading.key))}
          >
            {heading.sections.map((section) => (
              <React.Fragment key={section.sectionId}>
                <div id={sectionKeyFor(section.sectionTitle)} />
                <AttributesSection
                  {...section}
                  onChange={onChange}
                  highlightId={highlightId}
                  userEditingStrategy={props.userEditingStrategy}
                />
              </React.Fragment>
            ))}
          </HeadingSection>
        ))}

        <div className="editor__attributes-container__controls">
          <div className={cn({
            "editor__attributes-container__controls--notify": true,
            "editor__attributes-container__controls--error": savedHasError,
            "editor__attributes-container__controls--success": !savedHasError,
            "editor__attributes-container__controls--notify__visible": showSavedMessage
          })}><span>{savedMessage}</span></div>
          {props.userEditingStrategy === "update" ? (
            <>
              <SaveButton
                cy="save-button"
                disabled={saving}
                onSave={(e) => persistChanges(e)}
                id={props.saveButtonId}
              />
            </>
          ) : (
            <SecondaryButton
              cy="suggest-button"
              disabled={props.saving || props.disable}
              onClick={(e) => props.persistChanges(e)}
              id={props.suggestionsButtonId}
            >
              {"Suggest updates"}
            </SecondaryButton>
          )}
        </div>
      </div>
    </React.Fragment>
  );
}
